import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../ui/Modal';
import { AlertTriangle, Check, Info } from 'lucide-react';
import { useLanguage } from '../../i18n';
import {
    TOTAL_RECALL,
    indexFromTokens,
    isHighCost,
    isTotalRecall,
    limitsOf,
    maxIndex,
    pagesFromTokens,
    tickFractions,
    tokensFromIndex,
    wordsForTokens,
} from '../../utils/contextBudget';

// ─────────────────────────────────────────────────────────────────────────
// ContextThermostat — how much of the thread PAI holds while it answers (#349).
//
// ONE component, two scopes:
//   scope="global"  the Energy HUB — sets the default for NEW conversations
//   scope="chat"    inside the Energy Receipt — the conversation in front of you
//
// The chat scope is where the card's dual placement lives: moving the handle
// STAGES a value and then asks where it should apply ([Apply to this chat only]
// / [Set as Global Default]), because those are genuinely different intentions
// and guessing one of them is how a user silently changes every future chat.
//
// Nothing is written on drag. The handle is a draft until an explicit apply —
// which is also what makes the High Energy Usage confirmation possible: it
// guards the APPLY, not the dragging.
// ─────────────────────────────────────────────────────────────────────────

const FILL = {
    normal: '#6366f1',   // indigo-500 — the card's own colour
    high: '#f59e0b',     // amber — over the high-cost threshold
    max: '#f04438',      // red — Total Recall
};

const costLevel = (tokens, limits) => {
    if (isTotalRecall(tokens)) return 'max';
    return isHighCost(tokens, limits) ? 'high' : 'normal';
};

/** The value as the user reads it: "24 Pages" or "Total Recall". */
export const budgetLabel = (t, tokens, limits) => {
    if (isTotalRecall(tokens)) return t('thermostatTotalRecall', 'Total Recall');
    const pages = pagesFromTokens(tokens, limits);
    if (pages === null) return '—';
    return t('thermostatPages', '{n} Pages', { n: String(pages) });
};

// #537 follow-up: the bubble sits IN THE FLOW under the header (never an
// overlay), so it can never cover the slider, the apply buttons or the
// "Saved" line — it pushes them down instead. It opens and closes on click
// only (no hover-open: hover + click-toggle used to leave it stuck open).
const InfoBubble = React.forwardRef(({ t }, ref) => (
    <div
        ref={ref}
        id="thermostat-info-popover"
        data-testid="thermostat-info-popover"
        role="note"
        className="mt-2 w-full max-w-[340px] rounded-card border border-pai bg-pai-modal p-4 shadow-modal min-w-0"
    >
        <p className="text-[12.5px] leading-relaxed text-pai-secondary">
            {t('thermostatInfoBody', "This is PAI's memory budget. If you want PAI to remember a longer thread of your conversation, he has to hold more information in his head and 'think' through more details. This makes him smarter but uses more Energy per message.")}
        </p>
        <p className="mt-2 text-[12.5px] leading-relaxed text-pai-meta">
            {t('thermostatInfoPage', '1 Page = ~560 words of memory.')}
        </p>
        <p className="mt-2 text-[12.5px] leading-relaxed text-pai-meta">
            <strong className="text-white">{t('thermostatInfoLowLabel', 'Low Budget:')}</strong>{' '}
            {t('thermostatInfoLow', "PAI stays focused on 'now.' Saves Energy.")}
        </p>
        <p className="mt-1 text-[12.5px] leading-relaxed text-pai-meta">
            <strong className="text-white">{t('thermostatInfoHighLabel', 'High Budget:')}</strong>{' '}
            {t('thermostatInfoHigh', 'PAI holds the whole thread. Smarter, but costs more.')}
        </p>
    </div>
));
InfoBubble.displayName = 'InfoBubble';

/**
 * The High Energy Usage confirmation. Portaled — a thermostat can live inside
 * the receipt modal, which is itself portaled, and a `position: fixed` dialog
 * rendered inside a transformed ancestor is sized by that ancestor (#225).
 */
const HighCostDialog = ({ t, onCancel, onConfirm }) => {
    return (
        <Modal
            onClose={onCancel}
            zIndex="z-[90]"
            size="sm"
            role="alertdialog"
            ariaLabel={t('thermostatWarnTitle', 'High Energy Usage Warning')}
            cardClassName="p-7 min-w-0"
            cardTestId="thermostat-warning-dialog"
        >
                <h3 className="flex items-center gap-2 text-[17px] font-bold text-white">
                    <AlertTriangle size={17} className="text-pai-amber flex-shrink-0" />
                    {t('thermostatWarnTitle', 'High Energy Usage Warning')}
                </h3>
                <p className="mt-3 text-[13px] leading-relaxed text-pai-secondary">
                    {t('thermostatWarnBody', 'This will significantly increase the cost per message. Proceed?')}
                </p>
                <div className="mt-6 flex justify-end gap-2">
                    <button
                        type="button"
                        data-testid="thermostat-warning-cancel"
                        className="pai-btn-ghost"
                        onClick={onCancel}
                    >
                        {t('commonCancel', 'Cancel')}
                    </button>
                    <button
                        type="button"
                        data-testid="thermostat-warning-confirm"
                        className="pai-btn-primary"
                        onClick={onConfirm}
                    >
                        {t('commonConfirm', 'Confirm')}
                    </button>
                </div>
            </Modal>
    );
};

const ContextThermostat = ({
    scope = 'global',
    limits,
    value,               // stored tokens for this scope: null = inherit
    effectiveTokens,     // what is actually in force right now
    busy = false,
    error = null,
    onApply,             // (tokens, { global }) => Promise|void
}) => {
    const { t } = useLanguage();
    const L = useMemo(() => limitsOf(limits), [limits]);
    const top = useMemo(() => maxIndex(limits), [limits]);
    const ticks = useMemo(() => tickFractions(limits), [limits]);

    const settled = useMemo(
        () => indexFromTokens(value, limits, effectiveTokens),
        [value, limits, effectiveTokens],
    );
    const [index, setIndex] = useState(settled);
    const [showInfo, setShowInfo] = useState(false);
    const [pendingGlobal, setPendingGlobal] = useState(null);
    // #537: a visible "Saved" after a successful apply ('chat' | 'global'),
    // cleared by the next move of the handle or after a few seconds.
    const [saved, setSaved] = useState(null);
    const savedTimer = useRef(null);
    const dragging = useRef(false);

    useEffect(() => () => clearTimeout(savedTimer.current), []);

    // #537 follow-up: the info bubble closes on a click anywhere outside it
    // (after that click has done its job — a 'click' listener, not
    // 'pointerdown', so the layout never shifts under a press), on Esc, and
    // when the slider moves (onChange).
    const infoButtonRef = useRef(null);
    const infoBubbleRef = useRef(null);
    useEffect(() => {
        if (!showInfo) return undefined;
        const onClick = (e) => {
            const target = e.target;
            if (infoButtonRef.current?.contains(target)) return;
            if (infoBubbleRef.current?.contains(target)) return;
            setShowInfo(false);
        };
        // Esc closes the bubble ONLY: window capture runs before the Modal's
        // document-capture Escape handler, so the receipt / panel stays open.
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            e.stopPropagation();
            setShowInfo(false);
        };
        document.addEventListener('click', onClick);
        window.addEventListener('keydown', onKey, true);
        return () => {
            document.removeEventListener('click', onClick);
            window.removeEventListener('keydown', onKey, true);
        };
    }, [showInfo]);

    // Adopt a value that changed under us (a save landing, another surface
    // moving the global default) — but never mid-drag, which would yank the
    // handle out of the user's hand.
    useEffect(() => {
        if (!dragging.current) setIndex(settled);
    }, [settled]);

    const staged = tokensFromIndex(index, limits);
    const dirty = index !== settled;
    const level = costLevel(staged, limits);
    const words = wordsForTokens(staged, limits);
    const fillPct = (index / top) * 100;

    // onApply resolves to false when the save failed (the caller shows the
    // error); anything else counts as saved.
    const apply = async (global) => {
        setSaved(null);
        clearTimeout(savedTimer.current);
        let ok;
        try {
            ok = await onApply?.(staged, { global });
        } catch (_) {
            ok = false;
        }
        if (ok === false) return;
        setSaved(global ? 'global' : 'chat');
        savedTimer.current = setTimeout(() => setSaved(null), 6000);
    };

    const commit = (global) => {
        if (isHighCost(staged, limits)) {
            setPendingGlobal(global);
            return;
        }
        apply(global);
    };

    const confirmHighCost = () => {
        const global = pendingGlobal;
        setPendingGlobal(null);
        apply(global);
    };

    return (
        <section data-testid="context-thermostat" data-scope={scope} className="min-w-0">
            <header className="relative flex items-center justify-between gap-3 min-w-0">
                <span className="flex items-center gap-1.5 min-w-0">
                    <span className="text-[13px] font-semibold uppercase tracking-[0.08em] text-pai-meta truncate">
                        {t('thermostatTitle', 'Memory Budget')}
                    </span>
                    <button
                        type="button"
                        data-testid="thermostat-info"
                        aria-label={t('thermostatInfoAria', "About PAI's memory budget")}
                        aria-expanded={showInfo}
                        aria-controls="thermostat-info-popover"
                        ref={infoButtonRef}
                        onClick={() => setShowInfo((v) => !v)}
                        className="pai-tap touch:flex touch:items-center touch:justify-center flex-shrink-0 text-pai-meta hover:text-white transition-colors"
                    >
                        <Info size={14} />
                    </button>
                </span>
                <span
                    data-testid="thermostat-value"
                    data-tokens={staged}
                    data-total-recall={isTotalRecall(staged) ? 'true' : 'false'}
                    className="text-[15px] font-bold text-white tnum flex-shrink-0"
                >
                    {budgetLabel(t, staged, limits)}
                </span>
            </header>
            {showInfo && <InfoBubble ref={infoBubbleRef} t={t} />}

            {/* The track: drawn here, behind a transparent native input. */}
            <div className="relative mt-3 h-[22px] min-w-0">
                <div className="pointer-events-none absolute inset-x-0 top-1/2 h-[6px] -translate-y-1/2 rounded-full bg-white/[0.12]" />
                <div
                    data-testid="thermostat-fill"
                    data-cost={level}
                    className="pointer-events-none absolute left-0 top-1/2 h-[6px] -translate-y-1/2 rounded-full"
                    style={{ width: `${fillPct}%`, backgroundColor: FILL[level] }}
                />
                {ticks.map((fraction, i) => (
                    <span
                        key={i}
                        data-testid="thermostat-tick"
                        className="pointer-events-none absolute top-1/2 h-[10px] w-px -translate-y-1/2 bg-white/25"
                        style={{ left: `calc(${fraction * 100}% - 0.5px)` }}
                    />
                ))}
                <input
                    type="range"
                    data-testid="thermostat-slider"
                    data-cost={level}
                    className="pai-slider relative z-10 h-[22px] touch:h-10 touch:-my-[9px]"
                    min={0}
                    max={top}
                    step={1}
                    value={index}
                    disabled={busy}
                    aria-label={t('thermostatTitle', 'Memory Budget')}
                    aria-valuetext={budgetLabel(t, staged, limits)}
                    onChange={(e) => { setSaved(null); setShowInfo(false); setIndex(Number(e.target.value)); }}
                    onMouseDown={() => { dragging.current = true; }}
                    onTouchStart={() => { dragging.current = true; }}
                    onMouseUp={() => { dragging.current = false; }}
                    onTouchEnd={() => { dragging.current = false; }}
                    onBlur={() => { dragging.current = false; }}
                />
            </div>

            <div className="mt-1.5 flex items-baseline justify-between gap-3 text-[12px] text-pai-meta min-w-0">
                <span className="truncate">
                    {words === null
                        ? t('thermostatWholeThread', 'The whole conversation')
                        : t('thermostatWords', '~{n} words of memory', { n: words.toLocaleString() })}
                </span>
                <span data-testid="thermostat-effective" className="truncate tnum">
                    {t('thermostatInForce', 'In force: {v}', {
                        v: budgetLabel(t, effectiveTokens ?? L.defaultTokens, limits),
                    })}
                </span>
            </div>

            {error && (
                <p data-testid="thermostat-error" role="alert" className="mt-2 text-[12.5px] text-pai-red">
                    {error}
                </p>
            )}

            {saved && !error && !dirty && (
                <p
                    data-testid="thermostat-saved"
                    data-saved-scope={saved}
                    role="status"
                    className="mt-2 flex items-center gap-1.5 text-[12.5px] text-green-400 min-w-0"
                >
                    <Check size={13} className="flex-shrink-0" />
                    <span className="break-words min-w-0">
                        {saved === 'chat'
                            ? t('thermostatSavedChat', 'Saved for this chat only.')
                            : scope === 'chat'
                                ? t('thermostatSavedGlobal', 'Saved as your default for new chats and this one.')
                                : t('thermostatSavedDefault', 'Saved as your default for new chats.')}
                    </span>
                </p>
            )}

            {/* Where should this apply? Only asked once something was moved. */}
            {dirty && (
                <div data-testid="thermostat-actions" className="mt-4 flex flex-wrap gap-2 min-w-0">
                    {scope === 'chat' && (
                        <button
                            type="button"
                            data-testid="thermostat-apply-chat"
                            className="pai-btn-primary min-w-0"
                            disabled={busy}
                            onClick={() => commit(false)}
                        >
                            <span className="truncate">
                                {t('thermostatApplyChat', 'Apply to this chat only')}
                            </span>
                        </button>
                    )}
                    <button
                        type="button"
                        data-testid="thermostat-apply-global"
                        className={scope === 'chat' ? 'pai-btn-secondary min-w-0' : 'pai-btn-primary min-w-0'}
                        disabled={busy}
                        onClick={() => commit(true)}
                    >
                        <span className="truncate">
                            {t('thermostatApplyGlobal', 'Set as Global Default')}
                        </span>
                    </button>
                    <button
                        type="button"
                        data-testid="thermostat-reset"
                        className="pai-btn-ghost min-w-0"
                        disabled={busy}
                        onClick={() => setIndex(settled)}
                    >
                        <span className="truncate">{t('commonCancel', 'Cancel')}</span>
                    </button>
                </div>
            )}

            {pendingGlobal !== null && (
                <HighCostDialog
                    t={t}
                    onCancel={() => setPendingGlobal(null)}
                    onConfirm={confirmHighCost}
                />
            )}
        </section>
    );
};

export { TOTAL_RECALL };
export default ContextThermostat;
